<template>
  <div class="server-web-pressure">
    <div class="server-web-pressure__card">
      <div class="server-web-pressure__head">
        <h4>Web pressure</h4>
        <span v-if="collectedLabel" class="server-web-pressure__meta">{{ collectedLabel }}</span>
      </div>

      <!-- PHP-FPM pools: the signal that predicts an outage before any
           service check notices, because a pool at max_children is "up". -->
      <div class="server-web-pressure__section">
        <div class="server-web-pressure__section-title">PHP-FPM pools</div>
        <div v-if="pools.length" class="server-web-pressure__pools">
          <div v-for="pool in pools" :key="pool.key" class="server-web-pressure__pool">
            <div class="server-web-pressure__pool-head">
              <span class="server-web-pressure__pool-name">{{ pool.label }}</span>
              <span class="server-web-pressure__pool-figure" :class="pool.tone">
                <template v-if="pool.saturationPct !== null">
                  {{ pool.activeWorkers }}/{{ pool.maxChildren }} &middot; {{ Math.round(pool.saturationPct) }}%
                </template>
                <template v-else>{{ pool.activeWorkers }} worker(s)</template>
              </span>
            </div>
            <div class="server-web-pressure__bar">
              <span
                v-if="pool.saturationPct !== null"
                class="server-web-pressure__bar-fill"
                :class="pool.tone"
                :style="{ width: `${Math.min(100, pool.saturationPct)}%` }"
              ></span>
              <span v-else class="server-web-pressure__bar-unknown"></span>
            </div>
            <div class="server-web-pressure__pool-foot">
              <span>{{ pool.methodLabel }}</span>
              <span v-if="pool.listenQueue">queue {{ pool.listenQueue }}</span>
            </div>
          </div>
        </div>
        <div v-else class="server-web-pressure__empty">No PHP-FPM pools reported on this server.</div>
      </div>

      <!-- Per-vhost request rates: the view that makes a distributed crawl
           obvious even though no single client IP looks unusual. -->
      <div class="server-web-pressure__section">
        <div class="server-web-pressure__section-title">
          Request rate by vhost
          <span v-if="windowLabel" class="server-web-pressure__window">{{ windowLabel }}</span>
        </div>
        <div v-if="vhosts.length" class="server-web-pressure__vhosts">
          <div v-for="vhost in vhosts" :key="vhost.domain" class="server-web-pressure__vhost">
            <span class="server-web-pressure__vhost-name" :title="vhost.source">{{ vhost.domain }}</span>
            <span class="server-web-pressure__vhost-bar">
              <span
                class="server-web-pressure__vhost-fill"
                :class="vhost.tone"
                :style="{ width: `${vhost.share}%` }"
              ></span>
            </span>
            <span class="server-web-pressure__vhost-rate">{{ vhost.requestsPerMinute }}/min</span>
            <span class="server-web-pressure__vhost-clients">{{ vhost.clientsLabel }}</span>
          </div>
        </div>
        <div v-else class="server-web-pressure__empty">
          No web requests recorded in the last minute.
        </div>
      </div>

      <!-- Top CPU consumers, with php-fpm pool/vhost attribution — the
           readlink /proc/<pid>/cwd trick that traced the live incident. -->
      <div v-if="processes.length" class="server-web-pressure__section">
        <div class="server-web-pressure__section-title">Top CPU consumers</div>
        <div class="server-web-pressure__procs">
          <div v-for="proc in processes" :key="proc.pid" class="server-web-pressure__proc">
            <span class="server-web-pressure__proc-cpu">{{ proc.cpuPct }}%</span>
            <span class="server-web-pressure__proc-name">{{ proc.comm }}</span>
            <span class="server-web-pressure__proc-attr">{{ proc.attribution }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="server-web-pressure__card">
      <div class="server-web-pressure__head">
        <h4>Disk &amp; memory</h4>
      </div>
      <div v-if="filesystems.length" class="server-web-pressure__mounts">
        <div v-for="fs in filesystems" :key="fs.mount" class="server-web-pressure__mount">
          <span class="server-web-pressure__mount-name" :title="fs.device">{{ fs.mount }}</span>
          <span class="server-web-pressure__bar">
            <span class="server-web-pressure__bar-fill" :class="fs.tone" :style="{ width: `${fs.usedPct}%` }"></span>
          </span>
          <span class="server-web-pressure__mount-pct">{{ Math.round(fs.usedPct) }}%</span>
          <span class="server-web-pressure__mount-free">{{ fs.freeLabel }} free</span>
        </div>
      </div>
      <div v-else class="server-web-pressure__empty">No filesystem telemetry reported yet.</div>

      <div v-if="memory" class="server-web-pressure__section">
        <div class="server-web-pressure__section-title">Memory</div>
        <div class="server-web-pressure__mount">
          <span class="server-web-pressure__mount-name">RAM</span>
          <span class="server-web-pressure__bar">
            <span class="server-web-pressure__bar-fill" :class="memory.tone" :style="{ width: `${memory.usedPct}%` }"></span>
          </span>
          <span class="server-web-pressure__mount-pct">{{ Math.round(memory.usedPct) }}%</span>
          <span class="server-web-pressure__mount-free">{{ memory.availableLabel }} available</span>
        </div>
        <div v-if="memory.swapTotalBytes > 0" class="server-web-pressure__mount">
          <span class="server-web-pressure__mount-name">Swap</span>
          <span class="server-web-pressure__bar">
            <span class="server-web-pressure__bar-fill" :class="memory.swapTone" :style="{ width: `${memory.swapUsedPct}%` }"></span>
          </span>
          <span class="server-web-pressure__mount-pct">{{ Math.round(memory.swapUsedPct) }}%</span>
          <span class="server-web-pressure__mount-free">{{ memory.swapUsedLabel }} used</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

/**
 * Host resource-pressure panel — PHP-FPM pool saturation, per-vhost request
 * rates, top CPU consumers, per-mount disk and memory.
 *
 * Fed by `GET /api/v1/servers/{id}/pressure` (the latest `agent.heartbeat`
 * snapshot). Every block renders an honest empty state when the agent has
 * not reported that part, and NOTHING here fabricates a figure — a server
 * running a pre-2026-08-01 agent shows empty states, not zeroes.
 */
const props = defineProps({
  snapshot: { type: Object, default: null },
});

/**
 * Shared severity ramp. Amber from 75%, red from 90% — deliberately below
 * the detection rules' own thresholds (FPM 95%, CPU 1.25/core) so the UI
 * goes visibly warm BEFORE an alert fires, rather than turning red at the
 * same instant a notification lands.
 */
function tone(pct) {
  if (pct === null || pct === undefined) return 'is-unknown';
  if (pct >= 90) return 'is-critical';
  if (pct >= 75) return 'is-warning';
  return 'is-ok';
}

function formatBytes(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)}${units[unit]}`;
}

const collectedLabel = computed(() => {
  const at = props.snapshot?.collected_at;
  if (!at) return '';
  const seconds = Math.max(0, Math.round(Date.now() / 1000) - at);
  if (seconds < 90) return `updated ${seconds}s ago`;
  return `updated ${Math.round(seconds / 60)}m ago`;
});

const pools = computed(() => (props.snapshot?.fpm_pools || []).map((pool) => ({
  key: `${pool.phpVersion || '?'}:${pool.pool}`,
  label: pool.phpVersion ? `php${pool.phpVersion}/${pool.pool}` : pool.pool,
  activeWorkers: pool.activeWorkers ?? 0,
  maxChildren: pool.maxChildren ?? null,
  saturationPct: pool.saturationPct ?? null,
  listenQueue: pool.listenQueue ?? 0,
  tone: tone(pool.saturationPct),
  // Naming the measurement method is not pedantry: `process-count` reads
  // total spawned workers, which is a proxy for busy ones, and an operator
  // reading 100% deserves to know which number they are looking at.
  methodLabel: pool.method === 'status'
    ? 'measured from the FPM status page'
    : 'measured by worker-process count',
})));

const windowLabel = computed(() => {
  const seconds = props.snapshot?.web_pressure?.windowSeconds;
  return seconds ? `last ${seconds}s` : '';
});

const vhosts = computed(() => {
  const rows = props.snapshot?.web_pressure?.vhosts || [];
  const busiest = rows.reduce((max, row) => Math.max(max, row.requestsPerMinute || 0), 0);
  return rows.map((row) => ({
    domain: row.domain,
    source: row.source,
    requestsPerMinute: row.requestsPerMinute ?? 0,
    share: busiest > 0 ? Math.round(((row.requestsPerMinute ?? 0) / busiest) * 100) : 0,
    // 300/min is the `web.request_flood_by_vhost` threshold; colour the row
    // as it approaches so the card and the alert agree on what "hot" means.
    tone: tone(((row.requestsPerMinute ?? 0) / 300) * 100),
    clientsLabel: row.edgeOnly
      ? 'clients not visible (CF edge)'
      : `${row.distinctClients ?? 0} client(s)`,
  }));
});

const processes = computed(() => (props.snapshot?.top_processes || []).map((proc) => ({
  pid: proc.pid,
  cpuPct: Math.round(proc.cpuPct ?? 0),
  comm: proc.comm,
  attribution: proc.vhost
    ? `${proc.pool ? `pool ${proc.pool} · ` : ''}${proc.vhost}`
    : (proc.pool ? `pool ${proc.pool}` : (proc.cwd || `pid ${proc.pid}`)),
})));

const filesystems = computed(() => (props.snapshot?.filesystems || []).map((fs) => ({
  mount: fs.mount,
  device: fs.device,
  usedPct: fs.usedPct ?? 0,
  tone: tone(fs.usedPct),
  freeLabel: formatBytes(fs.availableBytes),
})));

const memory = computed(() => {
  const mem = props.snapshot?.memory;
  if (!mem) return null;
  return {
    usedPct: mem.usedPct ?? 0,
    tone: tone(mem.usedPct),
    availableLabel: formatBytes(mem.availableBytes),
    swapTotalBytes: mem.swapTotalBytes ?? 0,
    swapUsedPct: mem.swapUsedPct ?? 0,
    swapTone: tone(mem.swapUsedPct),
    swapUsedLabel: formatBytes(mem.swapUsedBytes),
  };
});
</script>

<style lang="scss">
.server-web-pressure {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 16px;
  align-items: start;
  margin-top: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  &__card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__head {
    padding: 14px 18px 10px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    justify-content: space-between;
    align-items: baseline;

    h4 {
      margin: 0;
      font-size: 13.5px;
      font-weight: 600;
    }
  }

  &__meta, &__window {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  &__window {
    margin-left: 6px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
  }

  &__section {
    padding: 12px 18px 14px;
    border-top: 1px solid var(--theme-border-soft);

    &:first-of-type {
      border-top: 0;
    }
  }

  &__section-title {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    margin-bottom: 10px;
  }

  &__empty {
    padding: 4px 0 2px;
    font-size: 12px;
    color: var(--theme-text-tertiary);
  }

  &__pools {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__pool-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 5px;
  }

  &__pool-name {
    font-size: 12px;
    font-weight: 600;
    font-family: var(--theme-font-mono);
  }

  &__pool-figure {
    font-size: 12px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;

    &.is-warning { color: var(--theme-status-warning); }
    &.is-critical { color: var(--theme-status-danger); }
  }

  &__pool-foot {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
  }

  &__bar {
    display: block;
    height: 8px;
    border-radius: 4px;
    background: var(--theme-border-soft);
    overflow: hidden;
    flex: 1;
  }

  &__bar-fill {
    display: block;
    height: 100%;
    border-radius: 4px;
    background: var(--theme-status-success);

    &.is-warning { background: var(--theme-status-warning); }
    &.is-critical { background: var(--theme-status-danger); }
  }

  &__bar-unknown {
    display: block;
    height: 100%;
    background: repeating-linear-gradient(45deg, var(--theme-border-soft), var(--theme-border-soft) 4px, var(--theme-bg-content) 4px, var(--theme-bg-content) 8px);
  }

  &__vhosts, &__procs, &__mounts {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__mounts {
    padding: 12px 18px 4px;
  }

  &__vhost, &__mount {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11.5px;
  }

  &__vhost-name, &__mount-name {
    flex: 0 0 34%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: var(--theme-font-mono);
  }

  &__vhost-bar {
    flex: 1;
    display: block;
    height: 6px;
    border-radius: 3px;
    background: var(--theme-border-soft);
    overflow: hidden;
  }

  &__vhost-fill {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: var(--theme-status-success);

    &.is-warning { background: var(--theme-status-warning); }
    &.is-critical { background: var(--theme-status-danger); }
  }

  &__vhost-rate, &__mount-pct {
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  &__vhost-clients, &__mount-free {
    flex: 0 0 auto;
    color: var(--theme-text-tertiary);
    font-size: 10.5px;
  }

  &__proc {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 11.5px;
  }

  &__proc-cpu {
    flex: 0 0 46px;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    text-align: right;
  }

  &__proc-name {
    flex: 0 0 auto;
    font-family: var(--theme-font-mono);
  }

  &__proc-attr {
    flex: 1;
    color: var(--theme-text-tertiary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
